import { useState } from 'react'
import { FormikProps, FormikTouched, setNestedObjectValues } from 'formik'
import { DateTime } from 'luxon'

import {
  Contract,
  CONTRACT_MODIFICATION_MAX_MONTHS_AHEAD,
  ContractTenant,
  ModifyContractInput,
} from '../../../../common/validations'
import { FORM_FIELD_VALIDATION_ERROR_CLASSNAME } from '../../../components/FormField/FormFieldConstants'
import { ContractChangesViewState } from '../types'
import { useConfirmationReturnParams } from './useConfirmationReturnParams'

type UseModifyContractFormParams = {
  contract: Contract
  tenants: ContractTenant[]
  setView: (view: ContractChangesViewState) => void
  onCancel: () => void
}

export const useModifyContractForm = ({
  contract,
  tenants,
  setView,
  onCancel,
}: UseModifyContractFormParams) => {
  const [movingOutTenantIds, setMovingOutTenantIds] = useState<number[]>([])

  useConfirmationReturnParams(setView)

  const now = DateTime.now()
  const inEffectDisabledBefore = now.startOf('day')
  const inEffectDisabledAfter = now.plus({
    months: CONTRACT_MODIFICATION_MAX_MONTHS_AHEAD,
  })

  const contractParties = tenants.filter((tenant) => tenant.isContractParty)

  const getPossibleNewContractParties = (moveOutTenantIds: number[]) =>
    tenants
      .filter((tenant) => 'isAdult' in tenant)
      .filter((tenant) => !tenant.isContractParty && tenant.isAdult)
      .filter((tenant) => !moveOutTenantIds.includes(tenant.id))

  const initialValues: ModifyContractInput = {
    inEffect: now.endOf('month').toFormat('yyyy-MM-dd'),
    contractPartyTenantIds: contractParties.map((tenant) => tenant.id),
    possibleContractPartyTenants: getPossibleNewContractParties([]),
    currentUserTenantId: contract.tenant.id,
    tenants,
    removeTenants: [],
  }

  const changeMovingOutTenants = async (
    form: FormikProps<ModifyContractInput>,
    tenantIds: number[]
  ) => {
    setMovingOutTenantIds(tenantIds)
    await form.setFieldValue(
      'possibleContractPartyTenants',
      getPossibleNewContractParties(tenantIds),
      false
    )
    await form.setFieldValue(
      'removeTenants',
      tenantIds.map((tenantId) => ({ tenantId })),
      false
    )
    await form.validateForm()
  }

  const showPreview = async (form: FormikProps<ModifyContractInput>) => {
    const errors = await form.validateForm()

    if (Object.keys(errors).length > 0) {
      // Mark fields with errors as touched to show error messages
      await form.setTouched(
        setNestedObjectValues<FormikTouched<ModifyContractInput>>(errors, true)
      )
      document
        .querySelector(`.${FORM_FIELD_VALIDATION_ERROR_CLASSNAME}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      window.scrollTo(0, 0)
      setView('preview')
    }
  }

  const cancel = (form: FormikProps<ModifyContractInput>) => {
    window.scrollTo(0, 0)
    form.resetForm()
    setMovingOutTenantIds([])
    onCancel()
  }

  return {
    now,
    inEffectDisabledBefore,
    inEffectDisabledAfter,
    movingOutTenantIds,
    contractParties,
    initialValues,
    getPossibleNewContractParties,
    changeMovingOutTenants,
    showPreview,
    cancel,
  }
}
